import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  CanDeactivateFn,
  GuardResult,
  MaybeAsync,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '@auth/services/authentication.service';
import { EventService } from '@services/event.service';
import { PermissionClientService } from '@services/permission-client.service';
import { UserRole } from '@openapi';

/**
 * Checks if the current user is authorized to access a given route URL.
 *
 * @param url - The URL path to check permissions for.
 * @returns An observable emitting `true` if authorized, or a `UrlTree` redirecting to login otherwise.
 */
const isUserAuthorized = (url: string): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const authenticationService = inject(AuthenticationService);
  const eventService = inject(EventService);
  const permissionService = inject(PermissionClientService);

  return permissionService.getPermissionForSpecificPermissionType('path').pipe(
    switchMap(permissions =>
      authenticationService.getUserRoles().pipe(
        map((userRoles: UserRole[]) => {
          if (!userRoles) {
            return router.createUrlTree(['/login']);
          }

          // Find permission config for the URL
          const permission = permissions.find(
            perm => perm.permission_type_definition === url
          );

          // Determine if any of the user’s roles match the allowed roles for the permission
          const isAuthorized = permission
            ? userRoles.some(role =>
                permission.roles.some(
                  allowedRole => role.userRole === allowedRole.rolename
                )
              )
            : true; // Default to true if no specific permission is defined

          eventService.emitRoleChange(userRoles);
          return isAuthorized || router.createUrlTree(['/login']);
        }),
        catchError(err => {
          console.error('AuthGuard error:', err);
          return of(router.createUrlTree(['/login']));
        })
      )
    )
  );
};

/**
 * Guard to control access to a route.
 *
 * @param route - The route snapshot.
 * @param state - The router state snapshot.
 * @returns Observable emitting `true` if access is granted, or a `UrlTree` for redirection.
 */
export const canActivate: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
  return route.url ? isUserAuthorized(route.url.toString()) : of(true);
};

/**
 * Guard to control access to child routes.
 * Delegates logic to `canActivate`.
 *
 * @param route - The child route snapshot.
 * @param state - The router state snapshot.
 * @returns Guard result as `boolean | UrlTree | Promise | Observable`.
 */
export const canActivateChild: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): MaybeAsync<GuardResult> => {
  return canActivate(route, state);
};

/**
 * Guard to determine if a component can be deactivated.
 * Always allows deactivation in this case.
 *
 * @returns Always `true`, allowing navigation away from the route.
 */
export const canDeactivate: CanDeactivateFn<unknown> = (
  component: unknown,
  currentRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
  nextState: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean => {
  return true;
};
